import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Power, X, Save } from "lucide-react";

/**
 * V1.30 · ANA · Configuración de productos comerciales.
 * Ubicación: Ajustes → ANA → Productos.
 * PIN sudo requerido para crear/editar/borrar.
 */
export default function AnaProductsSettings() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [mode, setMode] = useState("create");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ana/products");
      setProducts(data.products || []);
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setMode("create");
    setEditing({ slug: "", name: "", order: 50, color: "#6b7280", description: "", active: true });
    setEditorOpen(true);
  };
  const openEdit = (p) => {
    setMode("edit"); setEditing({ ...p }); setEditorOpen(true);
  };
  const save = async () => {
    if (!editing.name.trim()) { toast.error("Nombre obligatorio"); return; }
    if (mode === "create" && !editing.slug.trim()) { toast.error("Slug obligatorio"); return; }
    try {
      if (mode === "create") {
        await api.post("/ana/products", {
          slug: editing.slug.trim().toLowerCase().replace(/[^a-z0-9_]/g, "_"),
          name: editing.name, order: parseInt(editing.order || 50, 10),
          color: editing.color, description: editing.description,
          active: editing.active,
        });
        toast.success("Producto creado");
      } else {
        await api.put(`/ana/products/${editing.id}`, {
          name: editing.name, order: parseInt(editing.order || 50, 10),
          color: editing.color, description: editing.description,
          active: editing.active,
        });
        toast.success("Producto actualizado");
      }
      setEditorOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  const toggleActive = async (p) => {
    try {
      await api.put(`/ana/products/${p.id}`, { active: !p.active });
      toast.success(p.active ? "Producto desactivado" : "Producto activado");
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };
  const remove = async (p) => {
    if (p.is_system) { toast.error("Producto del sistema — desactívalo en su lugar"); return; }
    if (!window.confirm(`¿Borrar producto "${p.name}"? Requiere PIN sudo.`)) return;
    try {
      await api.delete(`/ana/products/${p.id}`);
      toast.success("Producto eliminado");
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  return (
    <div className="space-y-3" data-testid="ana-products-settings">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-lg">Productos comerciales</h3>
          <p className="text-xs text-neutral-500">Los productos que aparecerán al crear oportunidades. Cambios requieren PIN sudo.</p>
        </div>
        <button onClick={openCreate} className="btn-primary" data-testid="ana-products-create"><Plus className="h-4 w-4" /> Nuevo producto</button>
      </div>
      {loading ? (
        <div className="text-sm text-neutral-500">Cargando…</div>
      ) : (
        <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
          {products.map((p) => (
            <div key={p.id} className="rounded-lg border border-neutral-200 bg-white p-3" data-testid={`ana-product-card-${p.slug}`}>
              <div className="flex items-start justify-between gap-2 mb-1">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="h-3 w-3 rounded-full flex-shrink-0" style={{ backgroundColor: p.color }} />
                    <span className="font-semibold text-sm truncate">{p.name}</span>
                  </div>
                  <div className="text-[10px] text-neutral-500 font-mono truncate">{p.slug} · orden {p.order}</div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  {p.is_system && <span className="text-[9px] font-semibold text-neutral-500 bg-neutral-100 rounded-full px-1.5 py-0.5">SYS</span>}
                  <span className={"text-[9px] font-semibold rounded-full px-1.5 py-0.5 " + (p.active ? "bg-emerald-100 text-emerald-800" : "bg-neutral-200 text-neutral-600")}>{p.active ? "ACTIVO" : "INACTIVO"}</span>
                </div>
              </div>
              {p.description && <div className="text-xs text-neutral-600 mb-2">{p.description}</div>}
              <div className="flex items-center gap-1 pt-2 border-t border-neutral-100">
                <button onClick={() => openEdit(p)} className="text-[11px] font-semibold text-neutral-700 hover:bg-orange-50 rounded px-1.5 py-1 inline-flex items-center gap-1" data-testid={`ana-product-edit-${p.slug}`}><Pencil className="h-3 w-3" /> Editar</button>
                <button onClick={() => toggleActive(p)} className={"text-[11px] font-semibold inline-flex items-center gap-1 px-1.5 py-1 rounded " + (p.active ? "text-amber-700 hover:bg-amber-50" : "text-emerald-700 hover:bg-emerald-50")}><Power className="h-3 w-3" /> {p.active ? "OFF" : "ON"}</button>
                {!p.is_system && (
                  <button onClick={() => remove(p)} className="text-[11px] font-semibold text-red-700 hover:bg-red-50 rounded px-1.5 py-1 ml-auto" data-testid={`ana-product-delete-${p.slug}`}><Trash2 className="h-3 w-3" /></button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {editorOpen && editing && (
        <div className="fixed inset-0 z-50 bg-neutral-900/50 flex items-center justify-center p-4" onClick={() => setEditorOpen(false)}>
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-neutral-200 px-4 py-3">
              <h3 className="font-semibold text-lg">{mode === "create" ? "Nuevo producto" : `Editar: ${editing.name}`}</h3>
              <button onClick={() => setEditorOpen(false)} className="text-neutral-500 hover:text-neutral-900"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-4 space-y-3">
              {mode === "create" && (
                <div>
                  <label className="text-xs font-semibold text-neutral-700">Slug</label>
                  <input type="text" value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} data-testid="ana-product-editor-slug" className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-9 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-500" />
                </div>
              )}
              <div>
                <label className="text-xs font-semibold text-neutral-700">Nombre</label>
                <input type="text" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="ana-product-editor-name" className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-xs font-semibold text-neutral-700">Orden</label>
                  <input type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: parseInt(e.target.value || "50", 10) })} className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-neutral-700">Color</label>
                  <input type="color" value={editing.color} onChange={(e) => setEditing({ ...editing, color: e.target.value })} className="w-full rounded-lg border border-neutral-300 bg-white px-1 h-9" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-neutral-700">Estado</label>
                  <select value={editing.active ? "1" : "0"} onChange={(e) => setEditing({ ...editing, active: e.target.value === "1" })} className="w-full rounded-lg border border-neutral-300 bg-white px-3 h-9 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500">
                    <option value="1">Activo</option>
                    <option value="0">Inactivo</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-neutral-700">Descripción</label>
                <textarea value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} rows={2} className="w-full rounded-lg border border-neutral-300 bg-white p-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-neutral-200 px-4 py-3 bg-neutral-50">
              <button onClick={() => setEditorOpen(false)} className="btn-secondary">Cancelar</button>
              <button onClick={save} className="btn-primary" data-testid="ana-product-editor-save"><Save className="h-4 w-4" /> Guardar (PIN sudo)</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
